/**
 * Segment Controller v2 — Delivery Landing Page
 * Manages segment tile selection, URL hash detection, content toggling,
 * sticky mobile CTA bar (#1), and scroll depth tracking (#14).
 * No dependencies. Loaded with defer.
 */
(function () {
  'use strict';

  var SEGMENTS = ['default', 'ferme', 'volaille', 'chevaux', 'jardin', 'animaux', 'oiseaux'];
  var ACTIVE_CLASS = 'dlv-segment--active';
  var TILE_ACTIVE_CLASS = 'dlv-selector__tile--active';

  /* ----- SEGMENT SELECTION ----- */
  function init() {
    var tiles = document.querySelectorAll('[data-dlv-tile]');
    var segmentEls = document.querySelectorAll('[data-dlv-segment]');

    if (!tiles.length) return;

    // Bind click events on selector tiles
    tiles.forEach(function (tile) {
      tile.addEventListener('click', function () {
        activateSegment(tile.dataset.dlvTile, tiles, segmentEls);
        scrollToProductContent();
      });

      // Keyboard: Enter and Space activate
      tile.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          activateSegment(tile.dataset.dlvTile, tiles, segmentEls);
          scrollToProductContent();
        }
      });
    });

    // Hash handler — shared by initial load and hashchange
    function handleHashChange(scrollToContent) {
      var h = window.location.hash.replace('#', '').toLowerCase();
      if (SEGMENTS.indexOf(h) === -1) return;
      activateSegment(h, tiles, segmentEls);
      if (scrollToContent) {
        requestAnimationFrame(function () {
          var target = document.querySelector('.dlv-selector');
          if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      }
    }

    // On initial load, always start at the top of the page.
    // The hash selects the category but should NOT scroll mid-page.
    // The browser may have already scrolled to the hash anchor — force top.
    window.scrollTo(0, 0);

    // Extra frame to beat the browser's native hash scroll (which can fire after DOMContentLoaded)
    requestAnimationFrame(function () {
      window.scrollTo(0, 0);
      requestAnimationFrame(function () { window.scrollTo(0, 0); });
    });

    handleHashChange(false);

    // Default to generic 'default' state if no valid hash is present
    var h = window.location.hash.replace('#', '').toLowerCase();
    if (!h || SEGMENTS.indexOf(h) === -1) {
      activateSegment('default', tiles, segmentEls);
    }

    window.addEventListener('hashchange', function () { handleHashChange(false); });

    // Append UTM params to product tile links for attribution (I3)
    appendUtmToTileLinks();

    // Init sticky CTA bar (#1)
    initStickyCtaBar();

    // Init scroll depth tracking (#14)
    initScrollDepthTracking();
  }

  /* ----- SCROLL TO PRODUCT CONTENT (B10) ----- */
  function scrollToProductContent() {
    requestAnimationFrame(function () {
      // Scroll to the segment intro / product tiles area
      var intro = document.querySelector('.dlv-segment-intro.' + ACTIVE_CLASS);
      var target = intro || document.querySelector('.dlv-product-tiles');
      if (target) {
        var offset = 8; // small breathing room
        var y = target.getBoundingClientRect().top + window.pageYOffset - offset;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
    });
  }

  function activateSegment(segment, tiles, segmentEls) {
    if (SEGMENTS.indexOf(segment) === -1) return;

    // Update tile states
    tiles.forEach(function (t) {
      var isActive = t.dataset.dlvTile === segment;
      t.classList.toggle(TILE_ACTIVE_CLASS, isActive);
      t.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });

    // Toggle segment content visibility
    segmentEls.forEach(function (el) {
      el.classList.toggle(ACTIVE_CLASS, el.dataset.dlvSegment === segment);
    });

    // Update URL hash without scroll jump (no hash for 'default')
    try {
      if (window.history && window.history.replaceState) {
        if (segment === 'default') {
          history.replaceState(null, '', window.location.pathname);
        } else {
          history.replaceState(null, '', '#' + segment);
        }
      }
    } catch (e) { /* ignore — some browsers restrict replaceState */ }

    // Fire analytics event
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'delivery_segment_selected',
        segment: segment
      });
    }
  }

  /* ----- UTM ATTRIBUTION ON TILE LINKS (I3) ----- */
  function appendUtmToTileLinks() {
    var utm = 'utm_source=livraison&utm_medium=landing&utm_campaign=lancement';
    var links = document.querySelectorAll('.dlv-tile[href], .dlv-tiles__see-all[href]');
    links.forEach(function (a) {
      var href = a.getAttribute('href');
      if (href && href.indexOf('utm_source') === -1) {
        var sep = href.indexOf('?') === -1 ? '?' : '&';
        a.setAttribute('href', href + sep + utm);
      }
    });
  }

  /* ----- STICKY MOBILE CTA BAR (#1) ----- */
  function initStickyCtaBar() {
    var stickyBar = document.getElementById('dlv-sticky-cta');
    var selector = document.querySelector('.dlv-selector');
    if (!stickyBar || !selector || !('IntersectionObserver' in window)) return;

    var postcodeForms = document.querySelectorAll('[data-postcode-form]:not(.dlv-sticky-cta__form):not(.dlv-postcode--exit)');
    var selectorVisible = true;
    var formVisible = false;

    // Watch selector section — show sticky bar when it leaves viewport
    var selectorObserver = new IntersectionObserver(function (entries) {
      selectorVisible = entries[0].isIntersecting;
      toggleStickyBar(stickyBar, selectorVisible, formVisible);
    }, { threshold: 0 });
    selectorObserver.observe(selector);

    // Watch all postcode forms — hide sticky bar when any form is visible
    if (postcodeForms.length) {
      var formObserver = new IntersectionObserver(function (entries) {
        formVisible = entries.some(function (e) { return e.isIntersecting; });
        toggleStickyBar(stickyBar, selectorVisible, formVisible);
      }, { threshold: 0.3 });
      postcodeForms.forEach(function (form) { formObserver.observe(form); });
    }
  }

  function toggleStickyBar(bar, selectorVisible, formVisible) {
    // Show only on mobile (CSS handles hiding on desktop via display:none)
    // Show when: selector scrolled out of view AND no postcode form in view
    if (!selectorVisible && !formVisible) {
      bar.classList.add('dlv-sticky-cta--visible');
    } else {
      bar.classList.remove('dlv-sticky-cta--visible');
    }
  }

  /* ----- SCROLL DEPTH TRACKING (#14) ----- */
  function initScrollDepthTracking() {
    if (!('IntersectionObserver' in window) || !window.dataLayer) return;

    var milestones = [
      { pct: 25, fired: false },
      { pct: 50, fired: false },
      { pct: 75, fired: false },
      { pct: 100, fired: false }
    ];

    // Create sentinel elements at 25%, 50%, 75%, 100% of page
    function checkScrollDepth() {
      var scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      var docHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (docHeight <= 0) return;

      var pct = Math.round((scrollTop / docHeight) * 100);

      milestones.forEach(function (m) {
        if (!m.fired && pct >= m.pct) {
          m.fired = true;
          window.dataLayer.push({
            event: 'delivery_scroll_depth',
            scroll_depth: m.pct
          });
        }
      });
    }

    // Throttled scroll listener
    var scrollTimeout = null;
    window.addEventListener('scroll', function () {
      if (scrollTimeout) return;
      scrollTimeout = setTimeout(function () {
        checkScrollDepth();
        scrollTimeout = null;
      }, 200);
    }, { passive: true });
  }

  /* ----- INIT ----- */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
